import { useMemo, useEffect, useState } from "react";
import type { DataSnapshot } from "../lib/types";
import { useFilters } from "../store/filters";
import { BrandRail } from "../components/BrandRail";
import { SkillCard } from "../components/SkillCard";
import { skillMatchesFacetFilters } from "../lib/plugin-facet-filters";
import { STATUS_LABEL, TIER_LABEL } from "../lib/scoring";
import type { ScoreBucket, SkillStatus, Category } from "../lib/types";

interface Props { data: DataSnapshot }

export function Monitor({ data }: Props) {
  const {
    search, sort, buckets, statuses, categories, structures, facetFilters, minLines,
    setSearch, reset, toggleBucket, toggleStatus, toggleCategory, toggleStructure, setMinLines, setSort,
  } = useFilters();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [compact, setCompact] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.skills.filter((s) => {
      if (!buckets[s.bucket]) return false;
      if (!statuses[s.status]) return false;
      if (!categories[s.category]) return false;
      if (structures.router && !s.hasRouter) return false;
      if (structures.refs && !s.hasRefs) return false;
      if (structures.broken && s.brokenRefs.length === 0) return false;
      if (s.lineCount < minLines) return false;
      if (q) {
        const hay = [
          s.name,
          s.description,
          ...s.issues.map((i) => i.text),
          ...s.brokenRefs,
        ].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (!skillMatchesFacetFilters(s, facetFilters)) return false;
      return true;
    });
  }, [data.skills, search, buckets, statuses, categories, structures, facetFilters, minLines]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    switch (sort) {
      case "score-asc":  arr.sort((a, b) => a.score - b.score); break;
      case "score-desc": arr.sort((a, b) => b.score - a.score); break;
      case "name-asc":   arr.sort((a, b) => a.name.localeCompare(b.name)); break;
      case "lines-desc": arr.sort((a, b) => b.lineCount - a.lineCount); break;
      case "recent":     arr.sort((a, b) => (b.lastScored ?? "").localeCompare(a.lastScored ?? "")); break;
    }
    return arr;
  }, [filtered, sort]);

  const warningCount = data.stats.statusCounts?.warning ?? 0;
  const criticalCount = data.stats.statusCounts?.critical ?? 0;

  const hasActiveFilters = Object.keys(categories).some((k) => !categories[k as keyof typeof categories]) ||
    Object.keys(statuses).some((k) => !statuses[k as keyof typeof statuses]) ||
    Object.keys(buckets).some((k) => !buckets[k as keyof typeof buckets]) ||
    Number(minLines) > 0 || structures.router || structures.refs || structures.broken ||
    Object.keys(facetFilters).some((k) => facetFilters[k]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "SELECT" || t.tagName === "TEXTAREA")) return;
      if (e.key === "/") { e.preventDefault(); document.getElementById("search")?.focus(); return; }
      if (e.key === "Escape") {
        if (search) { setSearch(""); (document.getElementById("search") as HTMLInputElement | null)?.blur(); }
        return;
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [search, setSearch]);

  const average = Math.round(data.stats.average);

  return (
    <div className="grid min-h-screen" style={{ gridTemplateColumns: "220px 1fr" }}>
      <BrandRail counts={{ monitor: data.stats.total, issues: warningCount + criticalCount }} />

      <main className="flex flex-col min-h-screen min-w-0">
        {/* Top bar */}
        <div className="sticky top-0 z-20 bg-bg border-b border-fg">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-0 px-4 py-3 items-stretch sm:items-center">
            {/* Search */}
            <div className="flex-1 flex items-center gap-2">
              <input
                id="search"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search skills… /"
                className="flex-1 font-mono text-t-12 border-2 border-fg bg-bg px-3 py-2 min-w-0"
              />
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={reset}
                  className="font-mono text-t-10 uppercase px-2 py-2 border-2 border-fg text-bad hover:bg-bad hover:text-bg shrink-0"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Controls */}
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setFiltersOpen(!filtersOpen)}
                className={`font-mono text-t-10 uppercase px-3 py-2 border-2 border-fg ${filtersOpen || hasActiveFilters ? "bg-fg text-bg" : ""}`}
              >
                Filters{filtersOpen ? " ×" : ""}
              </button>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as typeof sort)}
                className="font-mono text-t-10 uppercase px-3 py-2 border-2 border-fg bg-bg appearance-none cursor-pointer"
              >
                <option value="score-desc">Score ↓</option>
                <option value="score-asc">Score ↑</option>
                <option value="name-asc">Name</option>
                <option value="lines-desc">Lines</option>
                <option value="recent">Recent</option>
              </select>
              <button
                type="button"
                onClick={() => setCompact(!compact)}
                className={`font-mono text-t-10 uppercase px-3 py-2 border-2 border-fg ${compact ? "bg-fg text-bg" : ""}`}
                title={compact ? "List view" : "Grid view"}
              >
                {compact ? "☰" : "▤"}
              </button>
            </div>
          </div>
        </div>

        {/* Filter popover */}
        {filtersOpen && (
          <div className="border-b border-fg bg-bg">
            <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 max-h-[60vh] overflow-y-auto">

              {/* Quality tier */}
              <FilterGroup label="Tier">
                {(["excellent", "good", "needs-work", "poor"] as ScoreBucket[]).map((k) => (
                  <FilterChip
                    key={k}
                    label={TIER_LABEL[k]}
                    count={data.stats.bucketCounts?.[k] ?? 0}
                    active={buckets[k]}
                    onClick={() => toggleBucket(k)}
                  />
                ))}
              </FilterGroup>

              {/* Status */}
              <FilterGroup label="Status">
                {(["healthy", "warning", "critical"] as SkillStatus[]).map((k) => (
                  <FilterChip
                    key={k}
                    label={STATUS_LABEL[k]}
                    count={data.stats.statusCounts?.[k] ?? 0}
                    active={statuses[k]}
                    onClick={() => toggleStatus(k)}
                  />
                ))}
              </FilterGroup>

              {/* Category */}
              <FilterGroup label="Category">
                {(["agent", "skill", "utrecht", "workflow", "other"] as Category[]).map((k) => (
                  <FilterChip
                    key={k}
                    label={k}
                    count={data.stats.categoryCounts?.[k] ?? 0}
                    active={categories[k]}
                    onClick={() => toggleCategory(k)}
                  />
                ))}
              </FilterGroup>

              {/* Structure */}
              <FilterGroup label="Structure">
                <FilterChip
                  label="Router"
                  active={structures.router}
                  onClick={() => toggleStructure("router")}
                />
                <FilterChip
                  label="Has refs"
                  active={structures.refs}
                  onClick={() => toggleStructure("refs")}
                />
                <FilterChip
                  label="Broken refs"
                  active={structures.broken}
                  onClick={() => toggleStructure("broken")}
                />
              </FilterGroup>

              {/* Line count */}
              <FilterGroup label={`Min lines: ${minLines}`}>
                <input
                  type="range"
                  min={0}
                  max={600}
                  step={10}
                  value={minLines}
                  onChange={(e) => setMinLines(Number(e.target.value))}
                  className="w-full accent-fg"
                />
              </FilterGroup>
            </div>
          </div>
        )}

        {/* Summary bar */}
        <div className="px-4 py-2 border-b border-rule flex flex-wrap items-center justify-between gap-x-6 gap-y-1">
          <span className="font-mono text-t-10 uppercase tracking-[0.14em] text-muted">
            {sorted.length} of {data.stats.total} skills
            {hasActiveFilters && (
              <span className="ml-2 text-accent font-bold">{filtered.length} filtered</span>
            )}
          </span>
          <span className="font-mono text-t-10 uppercase tracking-[0.14em] text-muted">
            avg {average}% · {data.stats.withLlm} with LLM
          </span>
        </div>

        {/* Skill cards */}
        <div className="flex-1 overflow-y-auto">
          {compact ? (
            <div className="divide-y divide-rule">
              {sorted.map((s) => (
                <SkillCard key={s.name} skill={s} compact />
              ))}
            </div>
          ) : (
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {sorted.map((s) => (
                <SkillCard key={s.name} skill={s} />
              ))}
            </div>
          )}
          {sorted.length === 0 && (
            <div className="flex items-center justify-center py-24 font-mono text-t-11 uppercase text-muted">
              No skills match
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="font-mono text-t-10 uppercase tracking-[0.14em] text-muted font-medium block mb-2">
        {label}
      </span>
      <div className="flex flex-col gap-1">
        {children}
      </div>
    </div>
  );
}

function FilterChip({ label, count, active, onClick }: { label: string; count?: number; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`font-mono text-t-11 px-2 py-1 border border-fg text-left flex justify-between gap-2 ${
        active ? "bg-fg text-bg" : "hover:bg-hover"
      }`}
    >
      <span className="capitalize">{label}</span>
      {count !== undefined && <span className="text-muted tabular-nums">{count}</span>}
    </button>
  );
}
